import {
  getFullCommitmentDetails,
  getQuestions,
  getQuestionsNew,
} from "../services/commitmentService";
import { getBadgeById } from "../services/badgeService";
import { cachedRequest } from "./requestCache";
import { resolveCommitmentApiId } from "./commitmentIds";

/** True when badge uses recurring nudge questions (not one-shot promise cards). */
export function isNudgeBadge(
  badge?: { type?: string } | null,
  commitmentType?: string
): boolean {
  const t = String(commitmentType ?? badge?.type ?? "")
    .trim()
    .toLowerCase();
  return t === "nudge";
}

function readNumber(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Best-effort parse when API omits nudgesToAnswer (e.g. "respond to 13 nudge questions"). */
export function inferNudgeTotalFromPromiseText(
  text: unknown
): number | undefined {
  const s = String(text ?? "").trim();
  if (!s) return undefined;
  const patterns = [
    /(\d+)\s+nudge\s+questions?\/requests?/i,
    /(\d+)\s+nudge\s+questions?/i,
    /respond\s+to\s+(\d+)\s+nudge/i,
    /answer\s+(\d+)\s+nudge/i,
    /(\d+)\s+nudges?\s+to\s+answer/i,
    /committed\s+to\s+answer\s+(\d+)/i,
    /(\d+)\s+nudge(?:\s+questions?)?(?:\s+|\s*\/|\.)/i,
    /(\d+)\s+(?:progress\s+update\s+)?nudges?/i,
    /every\s+weekday.*?(\d+)\s+weeks?/i,
  ];
  for (const pattern of patterns) {
    const match = s.match(pattern);
    if (match?.[1]) {
      const n = Number(match[1]);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return undefined;
}

/** Merge promise/description from row, extraData, and enriched API fields. */
export function normalizeNudgeCommitmentSource(
  commitment: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!commitment) return {};
  const extra = (commitment.extraData ?? {}) as Record<string, unknown>;
  const promiseText =
    commitment.promise ??
    extra.promise ??
    commitment.description ??
    extra.description ??
    extra.statement ??
    commitment.statement;
  const text =
    typeof promiseText === "string" && promiseText.trim()
      ? promiseText.trim()
      : undefined;
  return {
    ...commitment,
    promise: text ?? commitment.promise,
    description: text ?? commitment.description,
    extraData: extra,
  };
}

export function buildNudgeDisplaySource(
  commitment: Record<string, unknown> | null | undefined,
  overrides?: Record<string, unknown> | null
): Record<string, unknown> {
  const base = normalizeNudgeCommitmentSource(commitment);
  if (!overrides) return base;
  const mergedExtra = {
    ...((base.extraData ?? {}) as Record<string, unknown>),
    ...((overrides.extraData ?? {}) as Record<string, unknown>),
  };
  return normalizeNudgeCommitmentSource({
    ...base,
    ...overrides,
    responses: overrides.responses ?? base.responses,
    questionsAsked: overrides.questionsAsked ?? base.questionsAsked,
    questionsAnswered: overrides.questionsAnswered ?? base.questionsAnswered,
    totalNudges: overrides.totalNudges ?? base.totalNudges,
    nudgesToAnswer: overrides.nudgesToAnswer ?? base.nudgesToAnswer,
    nudgesLeft: overrides.nudgesLeft ?? base.nudgesLeft,
    extraData: mergedExtra,
  });
}

const ONE_SHOT_PROMISE_NAME_PATTERN = /promise\s+kept/i;

export function readBadgeDisplayName(
  row?: Record<string, unknown> | null
): string {
  if (!row) return "";
  const badge = row.badge as Record<string, unknown> | undefined;
  return String(badge?.name ?? row.title ?? "").trim();
}

/**
 * Promise Kept–style badges (including Slack variants) are one-shot promise
 * cards — a single fulfillment question, not recurring Nudge Requests.
 */
export function isOneShotPromiseBadge(
  row?: Record<string, unknown> | null
): boolean {
  if (!row) return false;

  const name = readBadgeDisplayName(row);
  const badge = (row.badge ?? {}) as Record<string, unknown>;
  const type = String(row.type ?? badge.type ?? "")
    .trim()
    .toLowerCase();

  // Promise Kept + Slack Promise Kept: one-shot (even when API marks type as nudge).
  if (ONE_SHOT_PROMISE_NAME_PATTERN.test(name)) return true;
  if (/slack/i.test(name) && /promise/i.test(name)) return true;

  // ON TRACK accountability series are recurring nudges — not one-shot.
  if (/^on\s+track/i.test(name)) return false;
  if (badge.questionsTimed === true) return false;
  if (type === "nudge") return false;

  return false;
}

/** Badge name indicates a Slack-originated commitment (any Slack variant). */
export function isSlackBadgeCommitment(
  row?: Record<string, unknown> | null
): boolean {
  return /slack/i.test(readBadgeDisplayName(row));
}

/** True when the badge/commitment should use recurring nudge Q&A workflows. */
export function badgeUsesRecurringNudges(
  row?: Record<string, unknown> | null
): boolean {
  if (!row || isOneShotPromiseBadge(row)) return false;
  if (isSlackBadgeCommitment(row)) return false;

  const badge = row.badge as { type?: string } | null | undefined;
  const type = String(row.type ?? badge?.type ?? "")
    .trim()
    .toLowerCase();

  return type === "p2p" || type === "nudge" || isNudgeBadge(badge, type);
}

/** Count the committer chose at acceptance (Accept modal / WhatsApp). */
export function resolveCommittedNudgeTotal(
  commitment: Record<string, unknown> | null | undefined
): number | undefined {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c || isOneShotPromiseBadge(c)) return undefined;
  const extra = (c.extraData ?? {}) as Record<string, unknown>;
  const badge = (c.badge ?? {}) as Record<string, unknown>;
  return (
    readNumber(c.questionsToAnswer) ??
    readNumber(c.questions_to_answer) ??
    readNumber(c.nudgesToAnswer) ??
    readNumber(extra.questionsToAnswer) ??
    readNumber(extra.questions_to_answer) ??
    readNumber(extra.nudgesToAnswer) ??
    readNumber(c.totalNudges) ??
    readNumber(extra.totalNudges) ??
    readNumber(badge.questionsToAnswer) ??
    readNumber(badge.questions_to_answer) ??
    readNumber(badge.nudgesToAnswer) ??
    readNumber(badge.totalNudges) ??
    inferNudgeTotalFromPromiseText(c.promise) ??
    inferNudgeTotalFromPromiseText(c.description)
  );
}

/** How many nudge cycles the committer has completed (not individual question strings). */
export function resolveNudgeCyclesAnsweredCount(
  commitment: Record<string, unknown> | null | undefined
): number {
  if (!commitment) return 0;
  const extra = (commitment.extraData ?? {}) as Record<string, unknown>;

  const responseLists = [commitment.responses, extra.responses];
  for (const raw of responseLists) {
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.length;
    }
  }

  const questionsAnswered = [
    commitment.questionsAnswered,
    extra.questionsAnswered,
  ].find(Array.isArray) as string[] | undefined;
  const questionsAsked = [
    commitment.questionsAsked,
    extra.questionsAsked,
  ].find(Array.isArray) as string[] | undefined;

  if (questionsAnswered || questionsAsked) {
    const slots = Math.max(
      questionsAnswered?.length ?? 0,
      questionsAsked?.length ?? 0
    );
    let answered = 0;
    for (let i = 0; i < slots; i++) {
      const ans = questionsAnswered?.[i];
      if (ans != null && String(ans).trim() !== "") {
        answered++;
      }
    }
    if (answered > 0 || slots > 0) {
      return answered;
    }
  }

  return (
    readNumber(commitment.questionsAnswered) ??
    readNumber(extra.questionsAnswered) ??
    0
  );
}

/** How many nudge cycles the committer has answered so far. */
export function resolveNudgesAnsweredCount(
  commitment: Record<string, unknown> | null | undefined
): number {
  return resolveNudgeCyclesAnsweredCount(commitment);
}

/** True for any commitment with recurring nudge Q&A (nudge badge or p2p with nudge history). */
export function isNudgingCommitment(
  commitment: Record<string, unknown> | null | undefined
): boolean {
  if (!commitment || isOneShotPromiseBadge(commitment)) return false;
  const badge = commitment.badge as { type?: string } | null | undefined;
  const type = commitment.type as string | undefined;
  if (isNudgeBadge(badge, type)) return true;

  const badgeType = String(type ?? badge?.type ?? "")
    .trim()
    .toLowerCase();
  const extra = (commitment.extraData ?? {}) as Record<string, unknown>;
  const hasHistory =
    (Array.isArray(commitment.responses) && commitment.responses.length > 0) ||
    (Array.isArray(commitment.questionsAsked) &&
      commitment.questionsAsked.length > 0) ||
    (Array.isArray(commitment.questionsAnswered) &&
      commitment.questionsAnswered.length > 0) ||
    (Array.isArray(extra.questionsAsked) && extra.questionsAsked.length > 0) ||
    (Array.isArray(extra.questionsAnswered) &&
      extra.questionsAnswered.length > 0);
  const hasNudgeCounts =
    resolveCommittedNudgeTotal(commitment) != null ||
    readNumber(commitment.nudgesLeft) != null ||
    readNumber(extra.nudgesLeft) != null;
  const hasNudgeText =
    inferNudgeTotalFromPromiseText(commitment.promise) != null ||
    inferNudgeTotalFromPromiseText(commitment.description) != null;

  return (
    (badgeType === "p2p" || badgeType === "nudge") &&
    (hasHistory || hasNudgeCounts || hasNudgeText)
  );
}

/** Commitment Portfolio → My Commitments: read-only nudge Q&A history for committers. */
export function isCommitterNudgeHistoryContext(options: {
  isCommitmentPortfolioPage?: boolean;
  isOthersCommitmentsSection?: boolean;
  isActivePromisesTab?: boolean;
  isMyBadgesTab?: boolean;
}): boolean {
  return !!(
    options.isCommitmentPortfolioPage &&
    !options.isOthersCommitmentsSection &&
    (options.isActivePromisesTab || options.isMyBadgesTab)
  );
}

export interface NudgeSummaryDisplay {
  total: number;
  answered: number;
  pending: number;
  allAnswered: boolean;
  statusText: string;
  progressLabel: string | null;
}

/**
 * Issuer-facing summary: total nudges committed, how many answered, whether
 * all are complete. Used on Promises Owed to Me expanded rows and details modal.
 */
export function resolveNudgeSummaryDisplay(
  commitment: Record<string, unknown> | null | undefined
): NudgeSummaryDisplay | null {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c || !isNudgingCommitment(c)) return null;

  const extra = (c.extraData ?? {}) as Record<string, unknown>;
  const answered = resolveNudgesAnsweredCount(c);
  let total = resolveCommittedNudgeTotal(c) ?? 0;

  // Infer total from remaining (committed − answered), never from Q&A list length.
  if (!total) {
    const remaining =
      readNumber(c.nudgesLeft) ?? readNumber(extra.nudgesLeft);
    if (typeof remaining === "number") {
      total = answered + remaining;
    }
  }

  const pending = total > 0 ? Math.max(0, total - answered) : 0;
  const allAnswered = total > 0 && answered >= total;

  let statusText: string;
  if (isCommitmentPendingOffer(c)) {
    if (total > 0) {
      statusText = `${total} committed nudge${total === 1 ? "" : "s"} included in this offer`;
    } else {
      statusText = "Recurring nudges included in this offer";
    }
  } else if (total > 0) {
    statusText = allAnswered
      ? `All ${total} committed nudge${total === 1 ? "" : "s"} answered`
      : `${answered} of ${total} committed nudge${total === 1 ? "" : "s"} answered so far`;
  } else if (answered > 0) {
    statusText =
      total > 0
        ? `${answered} of ${total} committed nudge${total === 1 ? "" : "s"} answered so far`
        : `${answered} nudge${answered === 1 ? "" : "s"} answered so far`;
  } else {
    statusText =
      total > 0
        ? `0 of ${total} committed nudge${total === 1 ? "" : "s"} answered so far`
        : "No nudges answered yet";
  }

  return {
    total,
    answered,
    pending,
    allAnswered,
    statusText,
    progressLabel: isCommitmentPendingOffer(c)
      ? formatOfferedNudgeTotalLabel(c)
      : formatNudgeProgressLabel(resolveDisplayNudgeProgress(c)),
  };
}

/**
 * Nudge progress for display on any nudging commitment: the current nudge
 * number and the total nudges committed. Returns null for non-nudge badges.
 * Reuses the same resolution logic as the Answer Nudge modal (pickProgress).
 */
export function resolveNudgeProgress(
  commitment:
    | ({ badge?: { type?: string } | null; type?: string } & Record<
        string,
        unknown
      >)
    | null
    | undefined
): { current: number; total: number } | null {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c || !isNudgingCommitment(c)) {
    return null;
  }
  return pickProgress(c);
}

/**
 * True when a nudge question is open and the committer should answer now.
 * Between cycles (answered, waiting for the next send) this is false.
 */
export function isActivelyAnsweringNudge(
  commitment: Record<string, unknown> | null | undefined
): boolean {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c) return false;

  const answered = resolveNudgeCyclesAnsweredCount(c);
  const extra = (c.extraData ?? {}) as Record<string, unknown>;
  const questionsAsked = [c.questionsAsked, extra.questionsAsked].find(
    Array.isArray
  ) as unknown[] | undefined;
  const askedCount = questionsAsked?.length ?? 0;
  if (askedCount > answered) {
    return true;
  }

  if (resolveOpenNudgeQuestionsFromCommitment(c).length > 0) {
    return true;
  }

  const p2p = String(c.p2pState ?? "")
    .toLowerCase()
    .replace(/_/g, "-");
  return (
    p2p === "answering" ||
    p2p === "answer" ||
    p2p === "answer-question" ||
    p2p === "waiting-for-answer"
  );
}

/**
 * Pending acceptance / initiated offers have not started the nudge cycle yet.
 * Show total nudges offered, not "Nudge 1 of Y".
 */
export function isCommitmentPendingOffer(
  commitment: Record<string, unknown> | null | undefined
): boolean {
  if (!commitment) return false;
  const status = String(commitment.status ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  return status === "pending-acceptance" || status === "initiated";
}

/** Total committed nudges on an offer awaiting acceptance (not cycle progress). */
export function formatOfferedNudgeTotalLabel(
  commitment: Record<string, unknown> | null | undefined
): string | null {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c || !isNudgingCommitment(c)) return null;

  let total = resolveCommittedNudgeTotal(c) ?? 0;
  if (!total) {
    total =
      inferNudgeTotalFromPromiseText(c.promise) ??
      inferNudgeTotalFromPromiseText(c.description) ??
      0;
  }
  if (total > 0) {
    return `${total} nudge${total === 1 ? "" : "s"} offered`;
  }
  return "Recurring nudges offered";
}

/**
 * Human label for nudge progress. "Nudge X of Y" when the committed total is
 * known, otherwise "Nudge X".
 */
export function formatNudgeProgressLabel(
  progress: { current: number; total: number } | null | undefined
): string | null {
  if (!progress) return null;
  if (progress.total > 0) {
    return `Nudge ${progress.current} of ${progress.total}`;
  }
  return `Nudge ${progress.current}`;
}

/**
 * Progress chip for list/table headers. On issuer-facing rows, hide the chip
 * between nudge cycles so we don't show "Nudge 2" before nudge 2 is sent.
 */
export function resolveDisplayNudgeProgressLabel(
  commitment: Record<string, unknown> | null | undefined,
  options?: { issuerView?: boolean; pendingOffer?: boolean }
): string | null {
  if (!commitment) return null;
  if (options?.pendingOffer || isCommitmentPendingOffer(commitment)) {
    return formatOfferedNudgeTotalLabel(commitment);
  }
  if (options?.issuerView) {
    const delivered = resolveNudgesDeliveredCount(commitment);
    if (delivered <= 0 && !isActivelyAnsweringNudge(commitment)) {
      return null;
    }
  }
  const summary = resolveNudgeSummaryDisplay(commitment);
  if (summary?.progressLabel) return summary.progressLabel;
  return formatNudgeProgressLabel(resolveDisplayNudgeProgress(commitment));
}

export function resolveBadgeId(source: {
  badge?: { _id?: string; id?: string } | string;
  badgeId?: string;
}): string {
  if (typeof source.badge === "string") {
    return source.badge.trim();
  }
  return String(
    source.badge?._id ?? source.badge?.id ?? source.badgeId ?? ""
  ).trim();
}

/** Split multi-part prompts (WhatsApp often sends "1. ... 2. ... 3. ..." in one string). */
export function splitQuestionText(text: string): string[] {
  const t = String(text || "").trim();
  if (!t) return [];

  const numbered = t.split(/\n(?=\d+\.\s)/).map((s) => s.trim()).filter(Boolean);
  if (numbered.length > 1) {
    return numbered.map((s) => s.replace(/^\d+\.\s*/, "").trim()).filter(Boolean);
  }

  const paragraphs = t.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  if (paragraphs.length > 1) {
    return paragraphs;
  }

  return [t];
}

function appendQuestionTexts(target: string[], raw: unknown): void {
  if (raw == null) return;
  if (typeof raw === "string") {
    target.push(...splitQuestionText(raw));
    return;
  }
  if (Array.isArray(raw)) {
    raw.forEach((item) => appendQuestionTexts(target, item));
    return;
  }
  if (typeof raw === "object") {
    const text = String(
      (raw as { question?: string; text?: string; prompt?: string }).question ??
        (raw as { question?: string; text?: string }).text ??
        (raw as { prompt?: string }).prompt ??
        ""
    ).trim();
    if (text) {
      target.push(...splitQuestionText(text));
    }
  }
}

/**
 * Questions the committer should answer right now (from list row or full detail).
 * Does not include full questionsAsked history unless nothing else is available.
 */
export function resolveQuestionsFromCommitment(commitment: any): string[] {
  if (!commitment) return [];

  const collected: string[] = [];
  appendQuestionTexts(collected, commitment.currentQuestion);
  appendQuestionTexts(collected, commitment.current_question);
  appendQuestionTexts(collected, commitment.question);
  appendQuestionTexts(collected, commitment.nextQuestion);
  appendQuestionTexts(collected, commitment.activeQuestion);
  appendQuestionTexts(collected, commitment.questions);

  const extra = commitment.extraData ?? {};
  appendQuestionTexts(collected, extra.currentQuestion);
  appendQuestionTexts(collected, extra.activeQuestion);
  appendQuestionTexts(collected, extra.nextQuestion);

  let merged = mergeQuestionTexts(collected);

  if (merged.length === 0) {
    const asked = extra.questionsAsked ?? commitment.questionsAsked;
    if (Array.isArray(asked) && asked.length > 0) {
      appendQuestionTexts(collected, asked[asked.length - 1]);
      merged = mergeQuestionTexts(collected);
    }
  }

  return merged;
}

/**
 * Question text for the nudge cycle open right now — not the full badge
 * question list on `commitment.questions`.
 */
function resolveOpenNudgeQuestionsFromCommitment(commitment: any): string[] {
  if (!commitment) return [];

  const collected: string[] = [];
  appendQuestionTexts(collected, commitment.currentQuestion);
  appendQuestionTexts(collected, commitment.current_question);
  appendQuestionTexts(collected, commitment.question);
  appendQuestionTexts(collected, commitment.nextQuestion);
  appendQuestionTexts(collected, commitment.activeQuestion);

  const extra = commitment.extraData ?? {};
  appendQuestionTexts(collected, extra.currentQuestion);
  appendQuestionTexts(collected, extra.activeQuestion);
  appendQuestionTexts(collected, extra.nextQuestion);

  let merged = mergeQuestionTexts(collected);
  if (merged.length > 0) return merged;

  const status = String(commitment.status ?? "")
    .toLowerCase()
    .replace(/_/g, "-");
  const isQuestionStatus =
    status === "question" ||
    status === "questions" ||
    status === "awaiting-answer";
  if (!isQuestionStatus) return [];

  const asked = (extra.questionsAsked ?? commitment.questionsAsked) as
    | unknown[]
    | undefined;
  const answered = resolveNudgeCyclesAnsweredCount(commitment);
  if (Array.isArray(asked) && asked.length > answered) {
    appendQuestionTexts(collected, asked[asked.length - 1]);
    return mergeQuestionTexts(collected);
  }

  return [];
}

/** Nudges actually sent/delivered to the committer (not the next planned cycle). */
export function resolveNudgesDeliveredCount(
  commitment: Record<string, unknown> | null | undefined
): number {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c) return 0;
  const extra = (c.extraData ?? {}) as Record<string, unknown>;
  const answered = resolveNudgeCyclesAnsweredCount(c);

  const questionsAsked = [c.questionsAsked, extra.questionsAsked].find(
    Array.isArray
  ) as unknown[] | undefined;
  const askedCount = questionsAsked?.length ?? 0;

  const responses = [c.responses, extra.responses].find(Array.isArray) as
    | unknown[]
    | undefined;
  const responseCount = responses?.length ?? 0;

  const delivered = Math.max(askedCount, responseCount);
  if (delivered > 0) return delivered;

  if (
    askedCount > answered ||
    resolveOpenNudgeQuestionsFromCommitment(c).length > 0
  ) {
    return Math.max(1, answered + 1);
  }

  const p2p = String(c.p2pState ?? "")
    .toLowerCase()
    .replace(/_/g, "-");
  if (
    (p2p === "answering" ||
      p2p === "answer" ||
      p2p === "answer-question" ||
      p2p === "waiting-for-answer") &&
    answered === 0 &&
    askedCount === 0
  ) {
    return 1;
  }

  return answered;
}

/**
 * Progress for list/banner labels: delivered nudge count of committed total.
 * Answer-modal flow keeps {@link pickProgress} for the open question cycle.
 */
export function resolveDisplayNudgeProgress(
  commitment: Record<string, unknown> | null | undefined
): { current: number; total: number } | null {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c || !isNudgingCommitment(c)) return null;

  let total = resolveCommittedNudgeTotal(c) ?? 0;
  if (!total) {
    const extra = (c.extraData ?? {}) as Record<string, unknown>;
    const remaining =
      readNumber(c.nudgesLeft) ?? readNumber(extra.nudgesLeft);
    const answered = resolveNudgeCyclesAnsweredCount(c);
    if (typeof remaining === "number") {
      total = answered + remaining;
    }
  }

  const delivered = resolveNudgesDeliveredCount(c);
  if (delivered <= 0 && total <= 0) {
    return pickProgress(c);
  }
  if (total > 0) {
    return {
      current: Math.min(total, Math.max(1, delivered)),
      total,
    };
  }
  if (delivered > 0) {
    return { current: delivered, total: 0 };
  }
  return null;
}

/** Merge list row + fetched details, then resolve the open question text. */
export function resolvePendingNudgeQuestionsForRow(
  listRow: any,
  fetchedDetails?: any | null
): string[] {
  if (!listRow) return [];
  const merged = fetchedDetails
    ? mergeCommitmentWithFullRecord(listRow, fetchedDetails)
    : listRow;
  return resolveQuestionsFromCommitment(merged);
}

/** User-facing hint when Today's question block has no text yet. */
export function pendingNudgeQuestionEmptyHint(
  listRow: any,
  options?: { detailsLoaded?: boolean; stillLoading?: boolean }
): string {
  const status = String(listRow?.status ?? "")
    .toLowerCase()
    .replace(/_/g, "-");
  if (status === "pending-acceptance") {
    return "Accept this commitment under Requests to Commit before nudge questions begin.";
  }
  if (options?.stillLoading) {
    return "Loading today's question…";
  }
  if (!options?.detailsLoaded) {
    return "Loading today's question…";
  }
  return "Today's question is not available yet. Tap Answer Nudge to open the answer form, or refresh if this just updated.";
}

/**
 * True when the committer has question text to answer right now (from the
 * commitment row or embedded badge config). Rows with nudge counts but no
 * admin-configured questions must not appear in Nudge Requests.
 */
export function commitmentHasAnswerableQuestion(
  commitment: Record<string, unknown> | null | undefined
): boolean {
  if (!commitment) return false;

  if (resolveQuestionsFromCommitment(commitment).length > 0) return true;

  const badge = (commitment.badge ?? {}) as Record<string, unknown>;
  const embeddedLists = [
    badge.questions,
    badge.nudgeQuestions,
    badge.questionSet,
    commitment.questions,
  ].filter(Array.isArray) as unknown[][];

  for (const list of embeddedLists) {
    if (mapRawQuestionsToTexts(list).length > 0) return true;
  }

  return false;
}

/**
 * Slack commitments belong on My Promises (Request Badge) — never Nudge Requests,
 * even when the server flags action-required or a question appears on the row.
 */
export function isSlackRequestBadgeOnlyCommitment(
  row?: Record<string, unknown> | null
): boolean {
  return isSlackBadgeCommitment(row);
}

export function parseFullCommitmentRecord(response: any): any | null {
  const body = response?.data;
  if (!body) return null;
  const data =
    body.results?.data ??
    body.results ??
    body.data ??
    body.commitment ??
    body;
  if (Array.isArray(data)) {
    return data[0] ?? null;
  }
  if (data && typeof data === "object") {
    return data;
  }
  return null;
}

/** GET /question workflow + controller shapes differ. */
export function extractQuestionsArray(response: any): any[] {
  const body = response?.data;
  if (!body) return [];
  const results = body.results;
  if (Array.isArray(results)) return results;
  if (results && Array.isArray(results.data)) return results.data;
  if (results && Array.isArray(results.results)) return results.results;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.questions)) return body.questions;
  return [];
}

export function extractQuestionsFromBadgeResponse(response: any): any[] {
  const body = response?.data;
  const badge =
    body?.badges ??
    body?.badge ??
    (Array.isArray(body?.results?.data) ? body.results.data[0] : null) ??
    body?.results?.data ??
    body?.results;
  const questions =
    badge?.questions ??
    badge?.nudgeQuestions ??
    badge?.questionSet ??
    body?.questions;
  return Array.isArray(questions) ? questions : [];
}

export function mapRawQuestionsToTexts(items: any[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  const add = (raw: string) => {
    const parts = splitQuestionText(raw);
    for (const t of parts) {
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  };

  items.forEach((q) => {
    if (typeof q === "string") {
      add(q);
      return;
    }
    if (!q || typeof q !== "object") return;
    const text = String(
      q.question ?? q.text ?? q.description ?? q.prompt ?? q.name ?? ""
    ).trim();
    if (text) add(text);
  });

  return out;
}

export function mergeQuestionTexts(...lists: string[][]): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const list of lists) {
    for (const t of list) {
      const n = t.trim();
      if (!n || seen.has(n)) continue;
      seen.add(n);
      merged.push(n);
    }
  }
  return merged;
}

/** Pre-defined nudge questions for a badge (GET /question, /questionNew, badge detail). */
export async function loadBadgeNudgeQuestions(
  badgeId: string,
  commitmentId?: string
): Promise<string[]> {
  if (!badgeId) return [];

  const cacheKey = `modal:nudge-questions:${badgeId}:${commitmentId || ""}`;
  return cachedRequest(cacheKey, async () => {
    const params: Record<string, string | number> = {
      page: 1,
      badge: badgeId,
      badgeId,
    };
    if (commitmentId) {
      params.commitment = commitmentId;
      params.commitmentId = commitmentId;
      params._id = commitmentId;
    }

    const [questionRes, questionNewRes, badgeRes] = await Promise.all([
      getQuestions(params).catch(() => null),
      getQuestionsNew(params).catch(() => null),
      getBadgeById(badgeId).catch(() => null),
    ]);

    return mergeQuestionTexts(
      mapRawQuestionsToTexts(extractQuestionsArray(questionRes)),
      mapRawQuestionsToTexts(extractQuestionsArray(questionNewRes)),
      mapRawQuestionsToTexts(extractQuestionsFromBadgeResponse(badgeRes))
    );
  });
}

/** Question + its scheduled / asked date, mirroring what WhatsApp shows. */
export interface NudgeQuestionItem {
  text: string;
  /** ISO string or pre-formatted label; null when no date is known. */
  date: string | null;
}

const DATE_FIELD_CANDIDATES = [
  "dueDate",
  "dueAt",
  "due",
  "scheduledAt",
  "scheduledFor",
  "scheduledDate",
  "askedAt",
  "askedDate",
  "askedOn",
  "promptAt",
  "promptedAt",
  "sentAt",
  "remindAt",
  "remindOn",
  "reminderDate",
  "reminderDueDate",
  "date",
  "timestamp",
  "createdAt",
] as const;

function extractDateFromRaw(raw: any): string | null {
  if (!raw || typeof raw !== "object") return null;
  for (const key of DATE_FIELD_CANDIDATES) {
    const v = (raw as Record<string, unknown>)[key];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
}

function extractTextFromRaw(raw: any): string {
  if (raw == null) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw !== "object") return "";
  const candidate =
    (raw as { question?: unknown }).question ??
    (raw as { text?: unknown }).text ??
    (raw as { prompt?: unknown }).prompt ??
    (raw as { description?: unknown }).description ??
    (raw as { name?: unknown }).name ??
    "";
  return String(candidate).trim();
}

function appendRichQuestion(target: NudgeQuestionItem[], raw: unknown): void {
  if (raw == null) return;

  if (Array.isArray(raw)) {
    raw.forEach((item) => appendRichQuestion(target, item));
    return;
  }

  if (typeof raw === "string") {
    splitQuestionText(raw).forEach((text) => {
      target.push({ text, date: null });
    });
    return;
  }

  if (typeof raw !== "object") return;

  const date = extractDateFromRaw(raw);
  const text = extractTextFromRaw(raw);
  if (!text) return;

  splitQuestionText(text).forEach((piece) => {
    target.push({ text: piece, date });
  });
}

function dedupeRichQuestions(items: NudgeQuestionItem[]): NudgeQuestionItem[] {
  const seen = new Set<string>();
  const out: NudgeQuestionItem[] = [];
  for (const q of items) {
    const t = q.text.trim();
    if (!t) continue;
    const key = `${t}|${q.date ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ text: t, date: q.date });
  }
  return out;
}

function richFromCommitmentRow(commitment: any): NudgeQuestionItem[] {
  if (!commitment) return [];
  const collected: NudgeQuestionItem[] = [];
  appendRichQuestion(collected, commitment.currentQuestion);
  appendRichQuestion(collected, commitment.current_question);
  appendRichQuestion(collected, commitment.question);
  appendRichQuestion(collected, commitment.nextQuestion);
  appendRichQuestion(collected, commitment.activeQuestion);
  appendRichQuestion(collected, commitment.questions);

  const extra = commitment.extraData ?? {};
  appendRichQuestion(collected, extra.currentQuestion);
  appendRichQuestion(collected, extra.activeQuestion);
  appendRichQuestion(collected, extra.nextQuestion);
  appendRichQuestion(collected, extra.questionsAsked);
  appendRichQuestion(collected, commitment.questionsAsked);

  return dedupeRichQuestions(collected);
}

function richFromBadgeResponse(response: any): NudgeQuestionItem[] {
  const body = response?.data;
  const badge =
    body?.badges ??
    body?.badge ??
    (Array.isArray(body?.results?.data) ? body.results.data[0] : null) ??
    body?.results?.data ??
    body?.results;
  const raw =
    badge?.questions ??
    badge?.nudgeQuestions ??
    badge?.questionSet ??
    badge?.remindersDueDate ??
    body?.questions;

  const collected: NudgeQuestionItem[] = [];
  appendRichQuestion(collected, raw);
  return dedupeRichQuestions(collected);
}

function richFromQuestionsArray(response: any): NudgeQuestionItem[] {
  const items = extractQuestionsArray(response);
  const collected: NudgeQuestionItem[] = [];
  appendRichQuestion(collected, items);
  return dedupeRichQuestions(collected);
}

async function loadRichBadgeQuestions(
  badgeId: string,
  commitmentId?: string
): Promise<NudgeQuestionItem[]> {
  if (!badgeId) return [];

  const cacheKey = `modal:rich-nudge-questions:${badgeId}:${commitmentId || ""}`;
  return cachedRequest(cacheKey, async () => {
    const params: Record<string, string | number> = {
      page: 1,
      badge: badgeId,
      badgeId,
    };
    if (commitmentId) {
      params.commitment = commitmentId;
      params.commitmentId = commitmentId;
      params._id = commitmentId;
    }

    const [questionRes, questionNewRes, badgeRes] = await Promise.all([
      getQuestions(params).catch(() => null),
      getQuestionsNew(params).catch(() => null),
      getBadgeById(badgeId).catch(() => null),
    ]);

    return dedupeRichQuestions([
      ...richFromQuestionsArray(questionRes),
      ...richFromQuestionsArray(questionNewRes),
      ...richFromBadgeResponse(badgeRes),
    ]);
  });
}

/**
 * Result of {@link loadAnswerModalState}. Carries both the questions to show
 * and the fully-enriched commitment (server + local merge). Callers MUST use
 * `enrichedCommitment` for submission so the server payload matches the
 * server's current view of the commitment (id, committer phone, current
 * question), even when the originating row was a stripped-down
 * `getActionRequired` item.
 */
export interface AnswerModalState {
  /** Full list of badge-configured questions; kept for callers that need them. */
  questions: NudgeQuestionItem[];
  /**
   * Single question the committer should answer right now. Prefer this over
   * `questions` in the Answer Nudge modal — WhatsApp asks one question per
   * cycle, not the whole set.
   */
  currentQuestion: NudgeQuestionItem | null;
  /**
   * Nudge sequence position. `total` may be 0 when the badge doesn't
   * advertise a fixed count; `current` is best-effort from answered count.
   */
  progress: { current: number; total: number } | null;
  /** ISO/string deadline for this nudge (commitment dueDate or per-question date). */
  answerByDate: string | null;
  /** Original commitment date (when the committer accepted the promise). */
  committedDate: string | null;
  /** Final due date for the overall commitment. */
  dueDate: string | null;
  enrichedCommitment: any;
  /** True when the commitment is still in a question-answerable state per server. */
  serverConfirmedAnswerable: boolean;
}

function pickProgress(
  commitment: any
): { current: number; total: number } | null {
  const c = normalizeNudgeCommitmentSource(commitment);
  if (!c) return null;
  const extra = (c.extraData ?? {}) as Record<string, unknown>;

  const answered = resolveNudgeCyclesAnsweredCount(c);
  const total = resolveCommittedNudgeTotal(c);
  const apiRemaining =
    readNumber(c.nudgesLeft) ?? readNumber(extra.nudgesLeft);
  const activelyAnswering = isActivelyAnsweringNudge(c);

  const cycleFromCounts = (
    committedTotal: number,
    remaining: number
  ): number => {
    const nextCycle = Math.min(
      committedTotal,
      Math.max(1, committedTotal - remaining + 1)
    );
    if (activelyAnswering) {
      return nextCycle;
    }
    if (answered > 0) {
      return Math.min(committedTotal, answered);
    }
    return 1;
  };

  // Have a total → standard "current of total".
  if (total && total >= 1) {
    const fromTotal = Math.max(0, total - answered);
    const r =
      typeof apiRemaining === "number"
        ? Math.max(fromTotal, apiRemaining)
        : fromTotal;
    return { current: cycleFromCounts(total, r), total };
  }

  // No declared total but we know remaining → infer total as answered+remaining.
  if (typeof apiRemaining === "number") {
    const fromApi = Math.max(0, apiRemaining);
    const inferred = Math.max(1, answered + fromApi);
    const current = activelyAnswering
      ? Math.min(inferred, Math.max(1, answered + 1))
      : Math.min(inferred, Math.max(1, answered || 1));
    return { current, total: inferred };
  }

  // Answered some cycles but total unknown — still avoid bare "Nudge N" when
  // the promise text states a committed count.
  if (answered > 0) {
    const fromText = resolveCommittedNudgeTotal(c);
    if (fromText && fromText >= 1) {
      const current = activelyAnswering
        ? Math.min(fromText, Math.max(1, answered + 1))
        : Math.min(fromText, answered);
      return { current, total: fromText };
    }
    return {
      current: activelyAnswering ? answered + 1 : Math.max(1, answered),
      total: 0,
    };
  }

  // First nudge — use committed total from API or promise text when available.
  const committedTotal = resolveCommittedNudgeTotal(c);
  if (committedTotal && committedTotal >= 1) {
    return { current: 1, total: committedTotal };
  }

  return { current: 1, total: 0 };
}

function pickStringFromCandidates(
  ...candidates: Array<unknown>
): string | null {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s && s !== "No due date" && s !== "No date") return s;
  }
  return null;
}

function pickAnswerByDate(
  commitment: any,
  currentQuestion: NudgeQuestionItem | null
): string | null {
  if (currentQuestion?.date) return currentQuestion.date;
  if (!commitment) return null;
  const extra = (commitment.extraData ?? {}) as Record<string, unknown>;
  return pickStringFromCandidates(
    extra.reminderDueDate,
    extra.remindAt,
    extra.reminderDate,
    extra.nextReminderDate,
    extra.nextReminderAt,
    commitment.reminderDueDate,
    commitment.remindAt,
    commitment.reminderDate,
    commitment.nextReminderDate,
    commitment.nextReminderAt,
    extra.dueDate,
    commitment.dueDate
  );
}

function pickCommittedDate(commitment: any): string | null {
  if (!commitment) return null;
  const extra = (commitment.extraData ?? {}) as Record<string, unknown>;
  return pickStringFromCandidates(
    commitment.commitmentDate,
    commitment.committedDate,
    extra.commitmentDate,
    extra.committedDate,
    commitment.acceptedAt,
    commitment.createdAt
  );
}

function pickDueDate(commitment: any): string | null {
  if (!commitment) return null;
  const extra = (commitment.extraData ?? {}) as Record<string, unknown>;
  return pickStringFromCandidates(
    commitment.dueDate,
    extra.dueDate,
    commitment.endDate,
    extra.endDate
  );
}

/**
 * Single "what should I answer right now?" question. Server's `currentQuestion`
 * wins; otherwise we fall back to the first item we know about so the modal
 * never shows the entire badge question set.
 */
function pickCurrentQuestion(
  commitment: any,
  questions: NudgeQuestionItem[]
): NudgeQuestionItem | null {
  if (!commitment) return questions[0] ?? null;
  const extra = (commitment.extraData ?? {}) as Record<string, unknown>;
  const rawCurrent =
    commitment.currentQuestion ??
    commitment.current_question ??
    commitment.question ??
    commitment.nextQuestion ??
    commitment.activeQuestion ??
    extra.currentQuestion ??
    extra.activeQuestion ??
    extra.nextQuestion;
  if (rawCurrent) {
    const collected: NudgeQuestionItem[] = [];
    appendRichQuestion(collected, rawCurrent);
    const cleaned = dedupeRichQuestions(collected);
    if (cleaned.length > 0) return cleaned[0];
  }
  return questions[0] ?? null;
}

const ANSWERABLE_STATUSES = new Set([
  "question",
  "questions",
  "awaiting-answer",
  "answer",
  "answering",
]);

function serverThinksAnswerable(record: any): boolean {
  if (!record || typeof record !== "object") return false;
  const status = String(record.status ?? "").toLowerCase().trim();
  if (ANSWERABLE_STATUSES.has(status)) return true;
  const p2pState = String(record.p2pState ?? "").toLowerCase().trim();
  if (
    p2pState === "answering" ||
    p2pState === "answer" ||
    p2pState === "answer-question" ||
    p2pState === "waiting-for-answer"
  ) {
    return true;
  }
  if (
    typeof record.currentQuestion === "string" &&
    record.currentQuestion.trim().length > 0
  ) {
    return true;
  }
  if (
    record.extraData &&
    typeof record.extraData.currentQuestion === "string" &&
    record.extraData.currentQuestion.trim().length > 0
  ) {
    return true;
  }
  return false;
}

/**
 * Merge list/action-required row with getFullCommitmentDetails record.
 * Preserves promise text and nudge counts from whichever source has them.
 */
export function mergeCommitmentWithFullRecord(
  commitment: any,
  record: any
): any {
  const initialId = resolveCommitmentApiId(commitment);
  const mergedExtra = {
    ...(commitment?.extraData ?? {}),
    ...(record?.extraData ?? {}),
  };
  return normalizeNudgeCommitmentSource({
    ...commitment,
    ...record,
    _id: resolveCommitmentApiId(record) || initialId,
    promise:
      record?.promise ??
      commitment?.promise ??
      commitment?.description ??
      record?.description,
    description:
      record?.description ??
      record?.promise ??
      commitment?.description ??
      commitment?.promise,
    questionsToAnswer:
      record?.questionsToAnswer ??
      record?.questions_to_answer ??
      commitment?.questionsToAnswer ??
      commitment?.questions_to_answer ??
      mergedExtra.questionsToAnswer,
    nudgesToAnswer:
      record?.nudgesToAnswer ??
      commitment?.nudgesToAnswer ??
      mergedExtra.nudgesToAnswer,
    totalNudges:
      record?.totalNudges ??
      commitment?.totalNudges ??
      mergedExtra.totalNudges,
    nudgesLeft:
      record?.nudgesLeft ??
      commitment?.nudgesLeft ??
      mergedExtra.nudgesLeft,
    badge: record?.badge ?? commitment?.badge,
    committer: record?.committer ?? commitment?.committer,
    issuer: record?.issuer ?? commitment?.issuer,
    responses: record?.responses ?? commitment?.responses,
    extraData: mergedExtra,
  });
}

/**
 * Fresh server snapshot for issuer Issue validation (list rows can be stale).
 */
export async function loadCommitmentForIssuerValidation(
  commitment: any
): Promise<any> {
  if (!commitment) return commitment;
  const initialId = resolveCommitmentApiId(commitment);
  if (!initialId) return commitment;
  const record = await fetchFullCommitmentRecordById(initialId);
  if (!record) return commitment;
  return mergeCommitmentWithFullRecord(commitment, record);
}

async function fetchFullCommitmentRecordById(
  commitmentId: string
): Promise<any | null> {
  try {
    const response = await getFullCommitmentDetails({
      _id: commitmentId,
      includeReminderAndLogs: true,
    });
    return parseFullCommitmentRecord(response);
  } catch {
    return null;
  }
}

/**
 * Load questions for the Answer Nudge modal AND enrich the commitment with
 * fresh server data when possible. Tries (in order):
 *   1. Questions in the row already (currentQuestion, badge prompts list).
 *   2. Badge-configured questions via /question + /questionNew + badge detail.
 *   3. /getFullCommitmentDetails for the freshest commitment (committer phone,
 *      current question, badge id) — used to enrich AND retried for questions.
 *
 * Returns the enriched commitment so the modal can submit against the same
 * snapshot it just rendered, matching what WhatsApp does.
 */
export async function loadAnswerModalState(
  commitment: any
): Promise<AnswerModalState> {
  const buildState = (
    questions: NudgeQuestionItem[],
    enrichedCommitment: any,
    serverConfirmedAnswerable: boolean
  ): AnswerModalState => {
    const currentQuestion = pickCurrentQuestion(enrichedCommitment, questions);
    return {
      questions,
      currentQuestion,
      progress: pickProgress(enrichedCommitment),
      answerByDate: pickAnswerByDate(enrichedCommitment, currentQuestion),
      committedDate: pickCommittedDate(enrichedCommitment),
      dueDate: pickDueDate(enrichedCommitment),
      enrichedCommitment,
      serverConfirmedAnswerable,
    };
  };

  if (!commitment) {
    return {
      questions: [],
      currentQuestion: null,
      progress: null,
      answerByDate: null,
      committedDate: null,
      dueDate: null,
      enrichedCommitment: commitment,
      serverConfirmedAnswerable: false,
    };
  }

  const initialId = resolveCommitmentApiId(commitment);
  let enriched = commitment;

  // Always enrich from full details first when possible — list/action-required
  // rows often have currentQuestion but omit questionsToAnswer / nudgesToAnswer.
  if (initialId) {
    const record = await fetchFullCommitmentRecordById(String(initialId));
    if (record) {
      enriched = mergeCommitmentWithFullRecord(commitment, record);
    }
  }

  // Questions from enriched row (currentQuestion, etc.).
  let questions = richFromCommitmentRow(enriched);

  // Badge-configured questions when the row still has none.
  if (questions.length === 0) {
    const badgeId = resolveBadgeId(enriched);
    if (badgeId) {
      questions = await loadRichBadgeQuestions(
        badgeId,
        resolveCommitmentApiId(enriched) || initialId || undefined
      );
    }
  }

  return buildState(
    questions,
    enriched,
    serverThinksAnswerable(enriched)
  );
}

/**
 * Convenience wrapper for callers that only need questions (date+text).
 * Mirrors what WhatsApp shows when the user picks a "Question to answer" item.
 */
export async function loadRichQuestionsForAnswerModal(
  commitment: any
): Promise<NudgeQuestionItem[]> {
  const state = await loadAnswerModalState(commitment);
  return state.questions;
}

/**
 * Questions for Answer Nudge modal: commitment fields, then full detail API, then badge config.
 * Matches what WhatsApp shows when the user picks a "Question to answer" item.
 */
export async function loadQuestionsForAnswerModal(
  commitment: any
): Promise<string[]> {
  const rich = await loadRichQuestionsForAnswerModal(commitment);
  return rich.map((q) => q.text);
}
